import { Injectable, Optional } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { firstValueFrom, Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import {
    ExternalResource,
    ExternalResourcesGroup,
    QuestionnaireGroupsConfig,
    QuestionnaireOperationBehaviorType,
    QuestionnaireOperationGroupType,
} from '@mona/models';
import { ExternalResourcesState } from '../entities';
import { ExternalResourcesActions, ExternalResourcesSelectors } from '../state';

/**
 * Notifications store service
 */
@Injectable({ providedIn: 'root' })
export class DataAccessExternalResourcesFacade {
    /** External links under applications section */
    readonly externalApplications$: Observable<ExternalResource[]> = this.store.select(
        ExternalResourcesSelectors.selectExternalApps,
    );

    /** External link groups embedded into encounter menu */
    readonly internalApplications$: Observable<ExternalResourcesGroup[]> = this.store.select(
        ExternalResourcesSelectors.selectInternalApps,
    );

    /** Get selected external resource */
    readonly selectedExternalApplication$: Observable<ExternalResource | null> = this.store.select(
        ExternalResourcesSelectors.selectSelectedExternalApp,
    );

    /** is daily goals data loading */
    readonly isLoading$ = this.store.select(ExternalResourcesSelectors.selectExternalResourcesLoading);

    /** is full screen mode */
    readonly isFullScreenMode$ = this.store.select(ExternalResourcesSelectors.selectIsFullScreenMode);

    /** get questionnaire groups config */
    readonly questionnaireGroupsConfig$: Observable<QuestionnaireGroupsConfig> = of({
        questionnaireGroups: [
            {
                id: 'mock-anaesthesia-group',
                isVisible: true,
                navigationTitle:
                    'Anaesthesia Module Anaesthesia Module Anaesthesia Module Anaesthesia Module Anaesthesia Module',
                navigationIcon: null,
                pageTitle:
                    'Anaesthesia Assessment Tools Anaesthesia Assessment Tools Anaesthesia Assessment Tools Anaesthesia Assessment Tools',
                order: 4,
                navigationUrl: 'custom-url',
                questionnaireSubGroups: [
                    {
                        order: 1,
                        displayName: 'Catheters Management',
                        isVisible: true,
                        questionnaireButtonText: 'All Catheters Custom Button Text and even more text',
                        icon: null,
                        tag: 'CM',
                        questionnaireColumnInformations: [
                            {
                                order: 1,
                                displayName: 'Name',
                                sourceKey: 'name',
                                valueType: 'string',
                                fallbackText: 'Not found',
                                isLinkId: false,
                                isVisible: true,
                                entityType: 'questionnaire',
                                questionnaireColumnsMapping: [],
                            },
                            {
                                order: 2,
                                displayName: 'Status',
                                sourceKey: 'mona_status',
                                valueType: 'valueBoolean',
                                fallbackText: 'N/A',
                                isLinkId: true,
                                isVisible: true,
                                entityType: 'questionnaireResponse',
                                questionnaireColumnsMapping: [
                                    {
                                        displayName: 'Active',
                                        fhirValue: 'true',
                                        isVisible: true,
                                    },
                                    {
                                        displayName: 'Inactive',
                                        fhirValue: 'false',
                                        isVisible: true,
                                    },
                                ],
                            },
                            {
                                order: 3,
                                displayName: 'Installation Date',
                                sourceKey: 'mona_installationdate',
                                valueType: ['valueDateTime', 'valueDate'],
                                fallbackText: 'N/A',
                                isLinkId: true,
                                isVisible: true,
                                entityType: 'questionnaireResponse',
                                questionnaireColumnsMapping: [],
                                customFormatting: {
                                    type: 'date',
                                    format: 'dd/MM/yyyy, HH:mm',
                                },
                            },
                        ],
                    },
                    {
                        order: 2,
                        displayName: 'Catheters Management 2',
                        isVisible: true,
                        questionnaireButtonText: 'All Catheters 2',
                        icon: null,
                        tag: 'score',
                        questionnaireColumnInformations: [
                            {
                                order: 1,
                                displayName: 'Status',
                                sourceKey: 'https://fhir.mona.icu/Questionnaire/Arterieller-Katheter',
                                valueType: 'questionnaire',
                                fallbackText: 'Questionnaire not available',
                                isLinkId: false,
                                isVisible: true,
                                entityType: 'questionnaire',
                                questionnaireColumnsMapping: [
                                    {
                                        displayName: 'Name',
                                        fhirValue: 'name',
                                        isVisible: true,
                                    },
                                    {
                                        displayName: 'Status',
                                        fhirValue: 'mona_status',
                                        isVisible: true,
                                    },
                                    {
                                        displayName: 'Installation Date',
                                        fhirValue: 'mona_installationdate',
                                        isVisible: true,
                                    },
                                ],
                            },
                        ],
                    },
                ],
                operationGroups: [
                    {
                        order: 1,
                        displayName: 'Filter by Type',
                        isVisible: true,
                        groupType: QuestionnaireOperationGroupType.Filter,
                        isAutofill: true,
                        operations: [
                            {
                                order: 1,
                                displayName: 'All',
                                isVisible: true,
                                isDefault: true,
                                sourceKey: 'name',
                                fhirValue: null,
                                valueType: 'string',
                                isLinkId: false,
                                entityType: 'questionnaire',
                            },
                        ],
                    },
                    {
                        order: 2,
                        displayName: 'Filter by Status',
                        isVisible: true,
                        groupType: QuestionnaireOperationGroupType.Filter,
                        isAutofill: true,
                        operations: [
                            {
                                order: 1,
                                displayName: 'All',
                                isVisible: true,
                                isDefault: true,
                                sourceKey: 'status',
                                fhirValue: null,
                                valueType: 'string',
                                isLinkId: false,
                                entityType: 'questionnaireResponse',
                                displayNamesMapping: {
                                    completed: 'Completed',
                                    stopped: 'Deleted',
                                },
                            },
                        ],
                    },
                    {
                        order: 3,
                        displayName: 'Filter by Catheter State',
                        isVisible: true,
                        groupType: QuestionnaireOperationGroupType.Filter,
                        operations: [
                            {
                                order: 1,
                                displayName: 'All',
                                isVisible: true,
                                isDefault: true,
                                sourceKey: null,
                                fhirValue: null,
                                valueType: null,
                                isLinkId: false,
                            },
                            {
                                order: 1,
                                displayName: 'Active',
                                isVisible: true,
                                isDefault: false,
                                sourceKey: 'mona_status',
                                fhirValue: true,
                                valueType: 'valueBoolean',
                                isLinkId: true,
                            },
                            {
                                order: 1,
                                displayName: 'Inactive',
                                isVisible: true,
                                isDefault: false,
                                sourceKey: 'mona_status',
                                fhirValue: false,
                                valueType: 'valueBoolean',
                                isLinkId: true,
                            },
                        ],
                    },
                    {
                        order: 4,
                        displayName: 'Group by',
                        isVisible: true,
                        groupType: QuestionnaireOperationGroupType.Group,
                        operations: [
                            {
                                order: 1,
                                displayName: 'No grouping',
                                isVisible: true,
                                isDefault: false,
                                sourceKey: null,
                                fhirValue: null,
                                valueType: null,
                                isLinkId: false,
                            },
                            {
                                order: 1,
                                displayName: 'Status',
                                isVisible: true,
                                isDefault: true,
                                sourceKey: 'mona_status',
                                fhirValue: null,
                                valueType: 'valueBoolean',
                                isLinkId: true,
                                groupFormattingType: 'string',
                                displayNamesMapping: {
                                    true: 'Active',
                                    false: 'Inactive',
                                },
                            },
                            {
                                order: 1,
                                displayName: 'Date',
                                isVisible: true,
                                isDefault: false,
                                sourceKey: 'authored',
                                fhirValue: null,
                                valueType: 'string',
                                isLinkId: false,
                                groupFormattingType: 'date',
                            },
                            {
                                order: 1,
                                displayName: 'Title',
                                isVisible: true,
                                isDefault: false,
                                sourceKey: 'title',
                                fhirValue: null,
                                valueType: 'string',
                                isLinkId: false,
                                entityType: 'questionnaire',
                            },
                        ],
                    },
                    {
                        order: 5,
                        displayName: 'Sort by',
                        isVisible: true,
                        groupType: QuestionnaireOperationGroupType.Sort,
                        operations: [
                            {
                                order: 1,
                                displayName: 'Alphabetical Order',
                                isVisible: true,
                                isDefault: true,
                                sourceKey: 'name',
                                fhirValue: null,
                                valueType: 'string',
                                isLinkId: false,
                                entityType: 'questionnaire',
                                behaviorType: QuestionnaireOperationBehaviorType.ExactMatch,
                            },
                            {
                                order: 1,
                                displayName: 'Bandage Change Date',
                                isVisible: true,
                                isDefault: false,
                                sourceKey: 'authored',
                                fhirValue: null,
                                valueType: 'date',
                                isLinkId: false,
                                entityType: 'questionnaireResponse',
                                behaviorType: QuestionnaireOperationBehaviorType.DateExact,
                            },
                            {
                                order: 1,
                                displayName: 'Installation Date',
                                isVisible: true,
                                isDefault: false,
                                sourceKey: 'mona_installationdate',
                                fhirValue: null,
                                valueType: 'valueDateTime',
                                isLinkId: true,
                                entityType: 'questionnaireResponse',
                                behaviorType: QuestionnaireOperationBehaviorType.DateExact,
                            },
                        ],
                    },
                ],
            },
        ],
    });

    /**
     * Constructor
     * @param store store
     */
    constructor(
        private store: Store<ExternalResourcesState>,
        @Optional() private actions$?: Actions,
    ) {}

    /**
     * get external resources
     */
    public getExternalResources(): void {
        this.store.dispatch(ExternalResourcesActions.loadExternalResources());
    }

    /**
     * clear external resources
     */
    public clearExternalResources(): void {
        this.store.dispatch(ExternalResourcesActions.clearExternalResources());
    }

    /**
     * select external resource
     * @param selected
     */
    public selectExternalResource(selected: ExternalResource): void {
        this.store.dispatch(ExternalResourcesActions.selectExternalResource({ selected }));
    }

    /**
     * clear selected external resource
     */
    public clearSelectedExternalResource(): void {
        this.store.dispatch(ExternalResourcesActions.clearSelectExternalResource());
    }

    /**
     * browser navigate back
     */
    public browserNavigateBack(): void {
        this.store.dispatch(ExternalResourcesActions.browserNavigateBackAction());
    }

    /**
     * browser navigate forward
     */
    public browserNavigateForward(): void {
        this.store.dispatch(ExternalResourcesActions.browserNavigateForwardAction());
    }

    /**
     * browser reload
     */
    public browserReload(): void {
        this.store.dispatch(ExternalResourcesActions.browserReloadAction());
    }

    /**
     * Reload the open external application at its start URL.
     * @param url application start URL
     */
    public browserNavigateHome(url: string): void {
        this.store.dispatch(ExternalResourcesActions.browserNavigateHomeAction({ url }));
    }

    /**
     * Close the external view. Pass true to also wipe its cookies and storage.
     * @param clearSession wipe the external-application session
     */
    public browserEndSession(clearSession = false): Promise<void> {
        if (!this.actions$) {
            return Promise.reject(new Error('Cannot await external session clearing without NgRx Actions'));
        }

        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const completion = firstValueFrom(
            this.actions$.pipe(
                ofType(
                    ExternalResourcesActions.browserEndSessionActionSuccess,
                    ExternalResourcesActions.browserEndSessionActionFailure,
                ),
                filter(action => action.requestId === requestId),
                take(1),
            ),
        ).then(action => {
            if (action.type === ExternalResourcesActions.browserEndSessionActionFailure.type) {
                throw action.error;
            }
        });

        // Subscribe before dispatch so even a fast IPC response cannot beat the waiter.
        this.store.dispatch(ExternalResourcesActions.browserEndSessionAction({ clearSession, requestId }));
        // Non-awaiting callers still surface the rejection through the failure action without an unhandled rejection.
        void completion.catch(() => undefined);
        return completion;
    }

    /**
     * browser maximize window
     * @param payload
     */
    public browserViewResize(payload): void {
        this.store.dispatch(ExternalResourcesActions.browserViewResize(payload));
    }

    /**
     * browser set window shown
     */
    public browserSetWindowShown(): void {
        this.store.dispatch(ExternalResourcesActions.browserSetWindowShownAction());
    }

    /**
     * browser set window hidden
     */
    public browserSetWindowHidden(): void {
        this.store.dispatch(ExternalResourcesActions.browserSetWindowHiddenAction());
    }

    /**
     * browser set to full screen
     */
    public browserEnterFullScreen(): void {
        this.store.dispatch(ExternalResourcesActions.browserEnterFullScreenAction());
    }

    /**
     * browser exit full screen
     */
    public browserExitFullScreen(): void {
        this.store.dispatch(ExternalResourcesActions.browserExitFullScreenAction());
    }
}
